import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, BookOpen, Check, Microscope, Sparkles } from "lucide-react";
import Navigation from "../components/Navigation";
import { AccessBanner } from "../components/research/AccessBanner";
import { ResearchBriefForm } from "../components/research/ResearchBriefForm";
import { DraftHistoryPanel } from "../components/research/DraftHistoryPanel";
import { DraftProgressPane } from "../components/research/DraftProgressPane";
import { DraftCard } from "../components/research/DraftCard";
import { ReviseCard } from "../components/research/ReviseCard";
import { VersionHistory } from "../components/research/VersionHistory";
import { EvidenceList } from "../components/research/EvidenceList";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResearchPage = ({ user }) => {
  const [prompt, setPrompt] = useState("");
  const [paperType, setPaperType] = useState("literature_review");
  const [language, setLanguage] = useState("English");
  const [citationStyle, setCitationStyle] = useState("APA");
  const [targetWords, setTargetWords] = useState("1800");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [openingDraftId, setOpeningDraftId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [jobStage, setJobStage] = useState("writing");
  const [elapsed, setElapsed] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const [reviseInstruction, setReviseInstruction] = useState("");
  const [reviseLoading, setReviseLoading] = useState(false);
  const [reviseRemaining, setReviseRemaining] = useState(null);
  const [docxLoadingId, setDocxLoadingId] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const [access, setAccess] = useState(null);
  const [includeOpenAccess, setIncludeOpenAccess] = useState(true);
  const [oaImports, setOaImports] = useState({});
  const [bulkImporting, setBulkImporting] = useState(false);
  const pollTimerRef = useRef(null);
  const revisePollRef = useRef(null);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/research/drafts`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(resp.data.drafts || []);
    } catch {
      /* history is non-blocking */
    }
  };

  const fetchAccess = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/research/access`, { headers: { Authorization: `Bearer ${token}` } });
      setAccess(resp.data);
    } catch {
      /* non-blocking */
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchHistory();
    fetchAccess();
    const activeJob = localStorage.getItem("research_active_job");
    if (activeJob) pollJob(activeJob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => () => {
    if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
    if (revisePollRef.current) window.clearTimeout(revisePollRef.current);
  }, []);

  useEffect(() => {
    if (!loading) return undefined;
    setElapsed(0);
    const timer = window.setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => window.clearInterval(timer);
  }, [loading]);

  const pollJob = (jobId) => {
    setLoading(true);
    setError("");
    localStorage.setItem("research_active_job", jobId);
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/research/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
        const job = resp.data;
        if (job.status === "completed" && job.project_id) {
          localStorage.removeItem("research_active_job");
          const detail = await axios.get(`${API}/research/drafts/${job.project_id}`, { headers: { Authorization: `Bearer ${token}` } });
          setResult(detail.data);
          setActiveProjectId(job.project_id);
          setLoading(false);
          fetchHistory();
          fetchAccess();
          window.setTimeout(() => document.getElementById("draft-result")?.scrollIntoView({ behavior: "smooth" }), 100);
          return;
        }
        if (job.status === "failed") {
          localStorage.removeItem("research_active_job");
          setError(job.error || "The AI Researcher could not create this draft. Please try again.");
          setLoading(false);
          return;
        }
        setJobStage(job.stage || "writing");
        if (Date.now() - startedAt > 10 * 60 * 1000) {
          localStorage.removeItem("research_active_job");
          setError("Draft generation timed out. Please try again.");
          setLoading(false);
          return;
        }
        pollTimerRef.current = window.setTimeout(tick, 3000);
      } catch (err) {
        const status = err.response?.status;
        if (status === 404 || status === 403) {
          localStorage.removeItem("research_active_job");
          setError("This draft job is no longer available.");
          setLoading(false);
          return;
        }
        pollTimerRef.current = window.setTimeout(tick, 5000);
      }
    };
    tick();
  };

  const generate = async (event) => {
    event.preventDefault();
    const topic = prompt.trim();
    if (topic.length < 20 || loading) return;
    setLoading(true);
    setError("");
    setResult(null);
    setJobStage("writing");
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API}/research/draft`,
        {
          prompt: topic,
          paper_type: paperType,
          language,
          citation_style: citationStyle,
          target_words: Number(targetWords),
          source_limit: 8,
          include_open_access: includeOpenAccess,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      pollJob(response.data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || "The AI Researcher could not create this draft. Please try again.");
      setLoading(false);
    }
  };

  const downloadDocx = async (projectId) => {
    if (docxLoadingId) return;
    setDocxLoadingId(projectId);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/research/drafts/${projectId}/docx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 120000,
      });
      const url = URL.createObjectURL(new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `archive-research-${projectId.slice(0, 8)}.docx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export this draft as a Word document. Please try again.");
    } finally {
      setDocxLoadingId(null);
    }
  };

  const undoRevision = async () => {
    if (!result?.project_id || undoLoading || reviseLoading) return;
    setUndoLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API}/research/drafts/${result.project_id}/undo-revision`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const detail = await axios.get(`${API}/research/drafts/${result.project_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setResult(detail.data);
      toast.success("Restored the previous version");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not undo the revision.");
    } finally {
      setUndoLoading(false);
    }
  };

  const pollRevision = (jobId) => {
    const tick = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/research/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
        const job = resp.data;
        if (job.status === "completed" && job.project_id) {
          const detail = await axios.get(`${API}/research/drafts/${job.project_id}`, { headers: { Authorization: `Bearer ${token}` } });
          setResult(detail.data);
          setReviseLoading(false);
          setReviseInstruction("");
          toast.success("Draft revised");
          return;
        }
        if (job.status === "failed") {
          setReviseLoading(false);
          toast.error(job.error || "The revision could not be applied. Please try again.");
          return;
        }
        revisePollRef.current = window.setTimeout(tick, 3000);
      } catch {
        revisePollRef.current = window.setTimeout(tick, 5000);
      }
    };
    tick();
  };

  const reviseDraft = async () => {
    const instruction = reviseInstruction.trim();
    if (!result?.project_id || instruction.length < 5 || reviseLoading) return;
    setReviseLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/research/drafts/${result.project_id}/revise`,
        { instruction },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (resp.data.remaining !== null && resp.data.remaining !== undefined) {
        setReviseRemaining(resp.data.remaining);
      }
      pollRevision(resp.data.job_id);
    } catch (err) {
      setReviseLoading(false);
      toast.error(err.response?.data?.detail || "The revision could not be started.");
    }
  };

  const importOpenAccess = async (refId) => {
    if (!result?.project_id || oaImports[refId]?.status === "importing") return;
    setOaImports((m) => ({ ...m, [refId]: { status: "importing" } }));
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/research/import-open-access`,
        { project_id: result.project_id, ref_id: refId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOaImports((m) => ({ ...m, [refId]: { status: "done", itemId: resp.data.item_id } }));
      if (resp.data.already_imported) {
        toast.success("This work is already in the library");
      } else if (resp.data.pdf_fetch === "queued") {
        toast.success("Added to the library — fetching the full-text PDF in the background");
      } else {
        toast.success("Added to the library (no free PDF available — metadata and abstract saved)");
      }
    } catch (err) {
      setOaImports((m) => {
        const copy = { ...m };
        delete copy[refId];
        return copy;
      });
      toast.error(err.response?.data?.detail || "Could not import this source.");
    }
  };

  const importAllOpenAccess = async () => {
    if (!result?.project_id || bulkImporting) return;
    setBulkImporting(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/research/import-open-access-bulk`,
        { project_id: result.project_id },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
      );
      const map = {};
      (resp.data.results || []).forEach((r) => {
        if (r.item_id) map[r.ref_id] = { status: "done", itemId: r.item_id };
      });
      setOaImports((m) => ({ ...m, ...map }));
      const parts = [`${resp.data.imported} imported`];
      if (resp.data.already_in_library) parts.push(`${resp.data.already_in_library} already in library`);
      if (resp.data.failed) parts.push(`${resp.data.failed} failed`);
      toast.success(
        `${parts.join(", ")}${resp.data.pdfs_queued ? ` — ${resp.data.pdfs_queued} full-text PDF${resp.data.pdfs_queued === 1 ? "" : "s"} fetching in the background` : ""}`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed.");
    } finally {
      setBulkImporting(false);
    }
  };

  const shareDraft = async () => {
    if (!result?.project_id) return;
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/research/drafts/${result.project_id}/share`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = `${window.location.origin}/research/shared/${resp.data.share_token}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        toast.success("Read-only share link copied to clipboard");
        window.setTimeout(() => setShareCopied(false), 2000);
      } catch {
        toast.info(url, { duration: 15000 });
      }
    } catch {
      toast.error("Could not create the share link.");
    }
  };

  const loadDraft = async (projectId) => {
    if (openingDraftId) return;
    setOpeningDraftId(projectId);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/research/drafts/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setResult(resp.data);
      setActiveProjectId(projectId);
      setOaImports({});
      window.setTimeout(() => document.getElementById("draft-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open this draft.");
    } finally {
      setOpeningDraftId(null);
    }
  };

  const downloadPdf = async (projectId) => {
    if (pdfLoadingId) return;
    setPdfLoadingId(projectId);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/research/drafts/${projectId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 120000,
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `archive-research-${projectId.slice(0, 8)}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export this draft as PDF. Please try again.");
    } finally {
      setPdfLoadingId(null);
    }
  };

  const renameDraft = async (projectId, title) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.patch(
        `${API}/research/drafts/${projectId}`,
        { title },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setHistory((h) => h.map((d) => (d.id === projectId ? { ...d, title } : d)));
      toast.success("Draft renamed");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not rename this draft.");
      return false;
    }
  };

  const deleteDraft = async (projectId) => {
    if (!window.confirm("Delete this draft? Its share link will stop working. This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/research/drafts/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory((h) => h.filter((d) => d.id !== projectId));
      if (activeProjectId === projectId) {
        setResult(null);
        setActiveProjectId(null);
      }
      toast.success("Draft deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete this draft.");
    }
  };

  const copyDraft = async () => {
    if (!result?.draft_markdown) return;
    await navigator.clipboard.writeText(result.draft_markdown);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const downloadDraft = () => {
    if (!result?.draft_markdown) return;
    const blob = new Blob([result.draft_markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `archive-research-${result.project_id.slice(0, 8)}.md`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="w-14 h-14 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mx-auto mb-5">
            <Microscope className="w-7 h-7" />
          </div>
          <h1 className="text-4xl font-semibold text-slate-950">AI Researcher</h1>
          <p className="mt-4 text-slate-600">Sign in to create evidence-grounded scientific drafts from The Archive.</p>
          <Button asChild className="mt-7 bg-slate-900 hover:bg-slate-800"><Link to="/login">Sign in</Link></Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      <Navigation user={user} />
      <main>
        <section className="relative overflow-hidden border-b border-slate-200 bg-slate-950 text-white">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #f97316 0, transparent 36%), radial-gradient(circle at 85% 80%, #0f766e 0, transparent 34%)" }} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-300 mb-5">
              <Sparkles className="w-4 h-4" /> The Archive · AI Researcher
            </div>
            <h1 className="text-4xl sm:text-6xl font-semibold max-w-4xl leading-[1.05]">Turn a research question into a cited scientific draft.</h1>
            <p className="mt-5 max-w-2xl text-slate-300 leading-7">
              The assistant searches your library, selects supporting passages, and writes only from the evidence it can trace.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid lg:grid-cols-[380px_1fr] gap-7 items-start">
            <div className="space-y-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1">
              <AccessBanner access={access} />
              <ResearchBriefForm
                prompt={prompt} setPrompt={setPrompt}
                paperType={paperType} setPaperType={setPaperType}
                language={language} setLanguage={setLanguage}
                citationStyle={citationStyle} setCitationStyle={setCitationStyle}
                targetWords={targetWords} setTargetWords={setTargetWords}
                includeOpenAccess={includeOpenAccess} setIncludeOpenAccess={setIncludeOpenAccess}
                error={error} loading={loading} access={access} onSubmit={generate}
              />
              <DraftHistoryPanel
                history={history}
                activeProjectId={activeProjectId}
                openingDraftId={openingDraftId}
                pdfLoadingId={pdfLoadingId}
                docxLoadingId={docxLoadingId}
                onOpen={loadDraft}
                onPdf={downloadPdf}
                onDocx={downloadDocx}
                onRename={renameDraft}
                onDelete={deleteDraft}
              />
            </div>

            <div id="draft-result" className="min-h-[560px]">
              {!result && !loading && (
                <div className="bg-white/70 border border-dashed border-slate-300 rounded-xl min-h-[560px] flex items-center justify-center p-10 text-center">
                  <div className="max-w-md">
                    <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                    <h2 className="text-2xl font-semibold text-slate-800">Your draft will appear here</h2>
                    <p className="mt-3 text-sm leading-6 text-slate-500">Each factual claim is tied to a library source marker. References are generated from stored metadata, not invented by the model.</p>
                  </div>
                </div>
              )}

              {loading && <DraftProgressPane jobStage={jobStage} elapsed={elapsed} />}

              {result && (
                <div className="space-y-6">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-3 text-sm text-emerald-900">
                    <Check className="w-5 h-5 shrink-0" />
                    <div>
                      <p className="font-semibold">Grounded draft created from {result.sources.length} sources.</p>
                      {(result.library_source_count !== undefined) && (
                        <p className="text-emerald-800 mt-0.5">
                          {result.library_source_count} from your library
                          {result.open_access_source_count ? ` · ${result.open_access_source_count} open-access` : ""}.
                        </p>
                      )}
                      <p className="text-emerald-700 mt-0.5">Review the linked evidence before academic submission.{result.remaining !== null ? ` ${result.remaining} draft${result.remaining === 1 ? "" : "s"} remaining today.` : ""}</p>
                    </div>
                  </div>

                  <DraftCard
                    result={result}
                    shareCopied={shareCopied}
                    copied={copied}
                    docxLoadingId={docxLoadingId}
                    pdfLoadingId={pdfLoadingId}
                    onShare={shareDraft}
                    onCopy={copyDraft}
                    onDownloadMarkdown={downloadDraft}
                    onDocx={downloadDocx}
                    onPdf={downloadPdf}
                  />

                  <ReviseCard
                    result={result}
                    access={access}
                    reviseInstruction={reviseInstruction}
                    setReviseInstruction={setReviseInstruction}
                    reviseLoading={reviseLoading}
                    reviseRemaining={reviseRemaining}
                    undoLoading={undoLoading}
                    onRevise={reviseDraft}
                    onUndo={undoRevision}
                  />

                  <VersionHistory
                    projectId={result.project_id}
                    revisionCount={result.revision_count}
                    onRestored={async () => {
                      const token = localStorage.getItem("access_token");
                      const detail = await axios.get(`${API}/research/drafts/${result.project_id}`, { headers: { Authorization: `Bearer ${token}` } });
                      setResult(detail.data);
                      toast.success("Version restored");
                    }}
                  />

                  <EvidenceList
                    result={result}
                    user={user}
                    oaImports={oaImports}
                    bulkImporting={bulkImporting}
                    onImportOne={importOpenAccess}
                    onImportAll={importAllOpenAccess}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 flex gap-3 text-sm text-amber-900">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <p><strong>Research integrity:</strong> this tool drafts literature-based text. It does not create experimental data, certify originality, or replace author review and journal disclosure requirements.</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ResearchPage;
